import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Графики раздела отчётов — рукописный SVG.
 *
 * В проекте нет библиотеки чартов, и заводить её ради трёх простых форм
 * не стоит: цвета берутся из CSS-переменных темы, поэтому графики без правок
 * работают во всех 12 темах, включая светлые.
 */

const PALETTE = ['var(--accent)', '#22d3ee', '#34d399', '#fbbf24', '#f472b6', '#a78bfa'];

export const chartColor = (i: number) => PALETTE[i % PALETTE.length];

const axisSx = {
  display: 'flex', justifyContent: 'space-between', padding: '4px 0 0',
  fontSize: 10, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace',
} as const;

/** Столбцы по дням: показы за период. */
export const BarChart: React.FC<{
  data: { bucket: string; plays: number }[];
  height?: number;
}> = ({ data, height = 130 }) => {
  if (!data.length) return <EmptyChart height={height} />;

  const max = Math.max(...data.map(d => d.plays), 1);
  const W = 620, H = 110, gap = 3;
  const bw = Math.max(2, W / data.length - gap);

  const label = (s: string) => {
    const d = new Date(s);
    return `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  return (
    <Box>
      <Box component="svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
        sx={{ width: '100%', height: `${height}px`, display: 'block' }}>
        {[0.25, 0.5, 0.75].map(f => (
          <line key={f} x1="0" y1={H * f} x2={W} y2={H * f} stroke="var(--divider)" strokeWidth="1" />
        ))}
        {data.map((d, i) => {
          const h = Math.max(1, (d.plays / max) * (H - 8));
          return (
            <rect key={d.bucket} x={i * (bw + gap)} y={H - h} width={bw} height={h}
              rx="2" fill="var(--accent)" opacity={0.85}>
              <title>{`${label(d.bucket)} — ${d.plays.toLocaleString('ru-RU')}`}</title>
            </rect>
          );
        })}
      </Box>
      <Box sx={axisSx}>
        <span>{label(data[0].bucket)}</span>
        {data.length > 2 && <span>{label(data[Math.floor(data.length / 2)].bucket)}</span>}
        <span>{label(data[data.length - 1].bucket)}</span>
      </Box>
    </Box>
  );
};

/** Профиль по часам: 24 столбца, показывает провалы внутри суток. */
export const HourChart: React.FC<{ hours: number[]; height?: number }> = ({ hours, height = 34 }) => {
  const max = Math.max(...hours, 1);
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: '2px', alignItems: 'flex-end' }}>
        {hours.map((v, h) => (
          <Box key={h} title={`${String(h).padStart(2, '0')}:00 — ${v}`}
            sx={{
              flex: 1, minWidth: '4px', height: `${height}px`, borderRadius: '2px',
              bgcolor: 'var(--accent)', opacity: v === 0 ? 0.06 : 0.25 + (v / max) * 0.75,
            }} />
        ))}
      </Box>
      <Box sx={axisSx}><span>00</span><span>06</span><span>12</span><span>18</span><span>23</span></Box>
    </Box>
  );
};

/** Горизонтальные полосы: распределение по файлам или плеерам. */
export const HBars: React.FC<{
  rows: { name: string; value: number }[];
  onRowClick?: (i: number) => void;
}> = ({ rows, onRowClick }) => {
  if (!rows.length) return <EmptyChart height={120} />;
  const max = Math.max(...rows.map(r => r.value), 1);

  return (
    <Box sx={{ py: '6px' }}>
      {rows.map((r, i) => (
        <Box key={r.name + i} onClick={() => onRowClick?.(i)}
          sx={{
            display: 'grid', gridTemplateColumns: '180px 1fr 74px', gap: '10px',
            alignItems: 'center', px: '18px', py: '7px',
            cursor: onRowClick ? 'pointer' : 'default',
            '&:hover': { bgcolor: 'var(--row-hover)' },
          }}>
          <Typography sx={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={r.name}>{r.name}</Typography>
          <Box sx={{ height: 16, borderRadius: '5px', bgcolor: 'var(--glass-hover)', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', borderRadius: '5px', width: `${(r.value / max) * 100}%`, bgcolor: chartColor(i) }} />
          </Box>
          <Typography sx={{ fontSize: 12, fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
            {r.value.toLocaleString('ru-RU')}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const EmptyChart: React.FC<{ height: number }> = ({ height }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ height: `${height}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}>{t('reports.noData')}</Typography>
    </Box>
  );
};
